import { create } from 'zustand';

export type PortalRole =
  | 'ROOT'
  | 'OPERATION'
  | 'CONTENT'
  | 'SYSTEM'
  | 'DOCTOR'
  | 'NURSE';

export interface AuthLoginPayload {
  email: string;
  name: string;
  // 0..N non-root roles. Root sends [] + isRoot=true.
  roles: string[];
  isRoot: boolean;
  // ad_admin.id is BIGINT UNSIGNED → number on the wire. Consumed by the self-edit guards in [AdminEditPanel].
  currentAdminId: number;
  // Account status code + the per-category permission map, both from GET /me.
  status: string;
  permissions: Record<string, string>;
  // Temporary password issued by an admin → forced reset.
  mustChangePassword: boolean;
}

// SHCT-629 — the permission-bearing slice of GET /me, re-applied on navigation so
// a Role change made elsewhere takes effect without a reload.
export interface AuthPermissionSync {
  roles: string[];
  isRoot: boolean;
  status: string;
  permissions: Record<string, string>;
}

// Subset of profile fields that may change while logged in (My Profile mutations).
export interface AuthProfilePatch {
  phone?: string | null;
  passwordChangedAt?: string | null;
  roles?: string[];
  name?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  email: string;
  name: string;
  roles: string[];
  isRoot: boolean;
  currentAdminId: number | null;
  status: string;
  permissions: Record<string, string>;
  mustChangePassword: boolean;
  phone: string | null;
  institution: string;
  joinedAt: string;
  passwordChangedAt: string | null;
  login: (payload: AuthLoginPayload) => void;
  logout: () => void;
  // SHCT-629 — re-apply the server's current answer for this admin. Called on
  // every protected navigation so a Role change lands without a reload.
  syncPermissions: (next: AuthPermissionSync) => void;
  // SHCT-629 (re-test) — the pathname whose permission read has SETTLED.
  //
  // The permission gate cannot judge a page until this equals the page's own path: before that,
  // `permissions` is whatever the previous navigation left behind, and acting on it accuses an
  // admin whose Role was just widened. Set on success AND on failure — a failed read still ends
  // the wait, and keeping the last known map is the documented safe failure mode.
  permissionsSyncedPath: string | null;
  markPermissionsSynced: (path: string) => void;
  setIsRoot: (isRoot: boolean) => void;
  setMustChangePassword: (value: boolean) => void;
  // Applied after My Profile mutations succeed so the GNB context stays in sync
  // without forcing a /auth/me refetch.
  setProfile: (patch: AuthProfilePatch) => void;
}

const initialState = {
  isAuthenticated: false,
  email: '',
  name: '',
  roles: [] as string[],
  isRoot: false,
  currentAdminId: null,
  status: '',
  permissions: {} as Record<string, string>,
  mustChangePassword: false,
  phone: null as string | null,
  institution: '',
  joinedAt: '',
  passwordChangedAt: null as string | null,
  permissionsSyncedPath: null as string | null,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,
  login: (payload) =>
    set({
      isAuthenticated: true,
      email: payload.email,
      name: payload.name,
      roles: payload.roles,
      isRoot: payload.isRoot,
      currentAdminId: payload.currentAdminId,
      status: payload.status,
      permissions: payload.permissions,
      mustChangePassword: payload.mustChangePassword,
      // Not part of GET /me; left at defaults until a My Profile fetch populates them.
      phone: null,
      institution: '',
      joinedAt: '',
      passwordChangedAt: null,
    }),
  logout: () => set({ ...initialState }),
  // SHCT-629 — the permission map was read once at login and never again, so an
  // admin whose Role changed kept the old menus and buttons until a full reload.
  // The server was always right (PRD 21 §7 L3 re-checks every call); it was the
  // client's L1/L2 layers that went stale, which reads as "the app let me click
  // it and then refused". Re-applying the server's answer keeps all three layers
  // agreeing.
  //
  markPermissionsSynced: (path) => set({ permissionsSyncedPath: path }),
  syncPermissions: (next) =>
    set({
      roles: next.roles,
      isRoot: next.isRoot,
      status: next.status,
      permissions: next.permissions,
    }),
  setIsRoot: (isRoot) => set({ isRoot }),
  setMustChangePassword: (value) => set({ mustChangePassword: value }),
  setProfile: (patch) =>
    set((state) => ({
      phone: patch.phone !== undefined ? patch.phone : state.phone,
      passwordChangedAt:
        patch.passwordChangedAt !== undefined
          ? patch.passwordChangedAt
          : state.passwordChangedAt,
      roles: patch.roles !== undefined ? patch.roles : state.roles,
      name: patch.name !== undefined ? patch.name : state.name,
    })),
}));
